// (c) chaehongee. Confidential and proprietary.

import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { analytics } from '../../services/analytics'

const MAX_SELECTED_NODES = 64

export interface SelectionState {
    selectedNodeIds: string[]
    hoveredNodeId?: string | null
    previewImageUrl?: string | null
    brushEnabled?: boolean
    hasMaskStrokes?: boolean
    // Used only for session entry (deep-link / sessions list) to avoid selecting a node
    // before canvas items are positioned.
    pendingInitialSelectionId?: string | null
}

interface SelectionSliceState {
    bySessionId: Record<string, SelectionState>
}

const initialSelectionState = (): SelectionState => ({
    selectedNodeIds: [],
    hoveredNodeId: null,
    previewImageUrl: null,
    brushEnabled: false,
    hasMaskStrokes: false,
    pendingInitialSelectionId: null,
})

const initialState: SelectionSliceState = { bySessionId: {} }

function getSelectionState(state: SelectionSliceState, sessionId: string): SelectionState {
    if (!state.bySessionId[sessionId]) {
        state.bySessionId[sessionId] = initialSelectionState()
    }
    return state.bySessionId[sessionId]
}

const selectionSlice = createSlice({
    name: 'selection',
    initialState,
    reducers: {
        addNode: (
            state,
            action: PayloadAction<{ sessionId: string; nodeId: string }>
        ) => {
            const s = getSelectionState(state, action.payload.sessionId)
            const id = action.payload.nodeId
            const isSelected = s.selectedNodeIds.includes(id)
            if (isSelected) return

            const next = [...s.selectedNodeIds, id]
            s.selectedNodeIds = next.slice(-MAX_SELECTED_NODES)
        },
        toggleNode: (
            state,
            action: PayloadAction<{ sessionId: string; nodeId: string }>
        ) => {
            const s = getSelectionState(state, action.payload.sessionId)
            const id = action.payload.nodeId
            const isSelected = s.selectedNodeIds.includes(id)
            if (isSelected) {
                s.selectedNodeIds = s.selectedNodeIds.filter((x) => x !== id)
                return
            }
            // Add up to 2 selections; keep most-recent two
            const next = [...s.selectedNodeIds, id]
            s.selectedNodeIds = next.slice(-MAX_SELECTED_NODES)
        },
        setHovered: (
            state,
            action: PayloadAction<{ sessionId: string; hoveredNodeId: string | null }>
        ) => {
            const s = getSelectionState(state, action.payload.sessionId)
            s.hoveredNodeId = action.payload.hoveredNodeId
        },
        setNodes: (
            state,
            action: PayloadAction<{ sessionId: string; nodeIds: string[] }>
        ) => {
            const s = getSelectionState(state, action.payload.sessionId)
            const ids = Array.isArray(action.payload.nodeIds) ? action.payload.nodeIds : []
            s.selectedNodeIds = ids.slice(0, MAX_SELECTED_NODES)

            // Track node selection
            if (ids.length > 0) {
                analytics.track('node_selected', {
                    session_id: action.payload.sessionId,
                    node_id: ids[0], // Track the first selected node
                    selection_count: ids.length,
                })
            }
        },
        setPendingInitialSelection: (
            state,
            action: PayloadAction<{ sessionId: string; nodeId: string | null }>
        ) => {
            const s = getSelectionState(state, action.payload.sessionId)
            s.pendingInitialSelectionId = action.payload.nodeId
        },
        clearPendingInitialSelection: (state, action: PayloadAction<{ sessionId: string }>) => {
            const s = getSelectionState(state, action.payload.sessionId)
            s.pendingInitialSelectionId = null
        },
        clear: (state, action: PayloadAction<{ sessionId: string }>) => {
            const s = getSelectionState(state, action.payload.sessionId)
            s.selectedNodeIds = []
        },
        setPreviewImageUrl: (
            state,
            action: PayloadAction<{ sessionId: string; previewImageUrl: string | null }>
        ) => {
            const s = getSelectionState(state, action.payload.sessionId)
            s.previewImageUrl = action.payload.previewImageUrl
        },
        clearPreviewImage: (state, action: PayloadAction<{ sessionId: string }>) => {
            const s = getSelectionState(state, action.payload.sessionId)
            s.previewImageUrl = null
        },
        setBrushEnabled: (
            state,
            action: PayloadAction<{ sessionId: string; brushEnabled: boolean }>
        ) => {
            const s = getSelectionState(state, action.payload.sessionId)
            s.brushEnabled = action.payload.brushEnabled
        },
        setHasMaskStrokes: (
            state,
            action: PayloadAction<{ sessionId: string; hasMaskStrokes: boolean }>
        ) => {
            const s = getSelectionState(state, action.payload.sessionId)
            s.hasMaskStrokes = action.payload.hasMaskStrokes
        },
        removeSelectionState: (state, action: PayloadAction<{ sessionId: string }>) => {
            delete state.bySessionId[action.payload.sessionId]
        },
    },
})

export const {
    addNode,
    toggleNode,
    setNodes,
    setPendingInitialSelection,
    clearPendingInitialSelection,
    clear,
    setHovered,
    setPreviewImageUrl,
    clearPreviewImage,
    setBrushEnabled,
    setHasMaskStrokes,
    removeSelectionState,
} = selectionSlice.actions
export default selectionSlice.reducer


